import { Component, effect, input, linkedSignal, model } from '@angular/core';
import { FormValueControl } from '@angular/forms/signals';
import { NgIcon } from '@ng-icons/core';
import { DebounceDirective } from '../../directives/debounce.directive';

@Component({
  selector: 'ui-input-phone',
  imports: [NgIcon, DebounceDirective],
  templateUrl: './input-phone.html',
})
export class InputPhone implements FormValueControl<string | null> {
  placeholder = input<string>('');
  inputId = input<string | null>(null);
  selectId = input<string | null>(null);
  autocomplete = input<string>('off');
  countryCodeLabel = input<string>('Country code');

  readonly value = model<string | null>(null);
  readonly disabled = input<boolean>(false);
  readonly touched = model<boolean>(false);
  readonly debounceMs = input<number | null>(null);

  protected countryCodes = input<string[]>([
    '+49',
    '+43',
    '+32',
    '+41',
    '+420',
    '+45',
    '+34',
    '+33',
    '+44',
    '+39',
    '+352',
    '+31',
    '+47',
    '+48',
    '+46',
    '+1',
  ]);

  protected internalData = linkedSignal<
    { value: string | null; countryCodes: string[] },
    { selectValue: string; textInputValue: string }
  >({
    source: () => ({
      value: this.value(),
      countryCodes: this.countryCodes(),
    }),
    computation: ({ value, countryCodes }, previous) => {
      const selectedCountryCode = countryCodes.find((countryCode) =>
        value?.startsWith(countryCode),
      );

      if (selectedCountryCode) {
        const textInputValue = value?.substring(selectedCountryCode.length) ?? '';

        // If the clean version of our previous text matches what we have now,
        // we preserve the previous dirty input (e.g. leading zero) to prevent the cursor jump.
        if (
          previous &&
          InputPhone.cleanTextInputValue(previous.value.textInputValue) === textInputValue &&
          previous.value.selectValue === selectedCountryCode
        ) {
          return previous.value;
        }

        return {
          selectValue: selectedCountryCode,
          textInputValue,
        };
      }

      return {
        selectValue: previous?.value.selectValue ?? countryCodes[0],
        textInputValue:
          value === null || value === '' ? '' : (previous?.value.textInputValue ?? ''),
      };
    },
  });

  constructor() {
    effect(() => this.value.set(this.getValue()));
  }

  protected updateSelectValue(value: string) {
    this.internalData.update((prev) => ({
      ...prev,
      selectValue: value,
    }));
  }

  protected displayTextInputValue = linkedSignal(() => this.internalData().textInputValue);

  protected updateTextInputValue(value: string) {
    this.internalData.update((prev) => ({
      ...prev,
      textInputValue: value,
    }));
  }

  protected onTextInputChange(value: string) {
    const cleaned = InputPhone.cleanTextInputValue(value);
    this.displayTextInputValue.set(cleaned);
    this.internalData.update((prev) => ({
      ...prev,
      textInputValue: cleaned,
    }));
  }

  private static cleanTextInputValue(input: string) {
    return input.replace(/^0+/, '').trim();
  }

  private getValue() {
    const data = this.internalData();
    const textInput = InputPhone.cleanTextInputValue(data.textInputValue);
    if (textInput.length === 0) {
      return '';
    }
    return `${data.selectValue}${textInput}`;
  }
}
